import classnames from 'classnames';
import React, { KeyboardEvent, ReactNode } from 'react';

import '@eika/transaction-list-style';

export interface FormattedTransaction {
    id: string;
    key?: string;
    description: string | ReactNode;
    date: string;
    amount: string;
    deposit: boolean;
    reservation: boolean;
    href?: string;
    onClick?: (id: string) => void;
    balance?: string;
    subDescription?: string;
}

export interface FormattedTransactions {
    id: string;
    label: string;
    subLabel?: string;
    transactions: FormattedTransaction[];
}

export interface TransactionListProps {
    className?: string;
    transactions: FormattedTransactions[];
    reservedText: string;
}

interface TransactionItemContainerProps {
    href?: string;
    onClick?: (id: string) => void;
    id?: string;
    className?: string;
    children: ReactNode;
}

const TransactionItemContainer = ({
    href,
    children,
    className,
    onClick,
    id,
}: TransactionItemContainerProps): JSX.Element => {
    if (href) {
        return (
            <a className={classnames('transaction-item-container', className)} href={href}>
                {children}
            </a>
        );
    }
    if (onClick && id) {
        const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.code === 'Enter') onClick(id);
        };
        return (
            <div
                className={classnames('transaction-item-container', className)}
                onClick={() => onClick(id)}
                onKeyDown={onKeyDown}
                role="button"
                tabIndex={0}
            >
                {children}
            </div>
        );
    }
    return <div className={classnames('transaction-item-container', className)}>{children}</div>;
};

const TransactionList = ({ className, reservedText, transactions }: TransactionListProps): JSX.Element => {
    return (
        <ul className={classnames('sgw-transaction-list', className)}>
            {transactions?.map((month) => (
                <li key={month.id} className="transaction-list__month">
                    <span className="month-year-label">
                        {month.label} {month.subLabel && <span className="saldo-label">{month.subLabel}</span>}
                    </span>
                    <div className="month-list">
                        {month.transactions.map((transaction) => (
                            <TransactionItemContainer
                                key={transaction.key ?? transaction.id}
                                href={transaction.href}
                                onClick={transaction.onClick}
                                id={transaction.id}
                                className={classnames({
                                    deposit: transaction.deposit,
                                    reservation: transaction.reservation,
                                    'centered-content': !transaction.balance && !transaction.subDescription,
                                })}
                            >
                                <div className="transactions-date">{transaction.date}</div>
                                {!transaction.reservation && !transaction.subDescription ? (
                                    <div className="transactions-name">
                                        <div className="max-two-lines">{transaction.description}</div>
                                    </div>
                                ) : (
                                    <div className="transactions-name max-one-line">
                                        <span className="max-one-line">{transaction.description}</span>
                                        {transaction.reservation ? (
                                            <span className="reserved-text max-one-line">{reservedText}</span>
                                        ) : (
                                            <span className="sub-description max-one-line">
                                                {transaction.subDescription}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="transactions-sum">
                                    {transaction.amount}
                                    {transaction.balance && (
                                        <span className="transaction-saldo">{transaction.balance}</span>
                                    )}
                                </div>
                            </TransactionItemContainer>
                        ))}
                    </div>
                </li>
            ))}
        </ul>
    );
};

export default TransactionList;
